import { memo, useState } from 'react';

import { BoardSelect, type BoardSelectProps } from '~/components/BoardSelect/BoardSelect';
import { useBoardSelectSearch } from '~/components/BoardSelect/useBoardSelectSearch';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type PrivateBoardSelectProps = Pick<
  BoardSelectProps,
  | 'children'
  | 'getBoardDisabledMessage'
  | 'getLibraryDisabledMessage'
  | 'initialBoard'
  | 'isBoardDisabled'
  | 'isBoardSelected'
  | 'isLibraryDisabled'
  | 'isLibrarySelected'
  | 'onCreateBoard'
  | 'onSelectBoard'
  | 'onSelectLibrary'
  | 'allowCreateSubBoard'
  | 'shouldShowSubBoards'
  | 'topContent'
>;

export const PrivateBoardSelect = memo(
  ({
    children,
    getBoardDisabledMessage,
    getLibraryDisabledMessage,
    initialBoard,
    isBoardDisabled,
    isBoardSelected,
    isLibraryDisabled,
    isLibrarySelected,
    onCreateBoard,
    onSelectBoard,
    onSelectLibrary,
    allowCreateSubBoard,
    shouldShowSubBoards,
    topContent,
  }: PrivateBoardSelectProps) => {
    const [search, setSearch] = useState('');
    const { libraries: librariesFlag } = usePrivateAddOnFeatures();
    const { currentWorkspace } = useCurrentWorkspace();
    const { boards, libraries } = useBoardSelectSearch({
      search,
      hasLibraries: librariesFlag,
      workspaceId: currentWorkspace?.id,
    });

    return (
      <BoardSelect
        boards={boards}
        getBoardDisabledMessage={getBoardDisabledMessage}
        getLibraryDisabledMessage={getLibraryDisabledMessage}
        hasLibraries={librariesFlag}
        initialBoard={initialBoard}
        isBoardDisabled={isBoardDisabled}
        isBoardSelected={isBoardSelected}
        isLibraryDisabled={isLibraryDisabled}
        isLibrarySelected={isLibrarySelected}
        libraries={libraries}
        onCreateBoard={onCreateBoard}
        onSearchChange={setSearch}
        onSelectBoard={onSelectBoard}
        onSelectLibrary={onSelectLibrary}
        allowCreateSubBoard={allowCreateSubBoard}
        search={search}
        shouldShowSubBoards={shouldShowSubBoards}
        topContent={topContent}
      >
        {children}
      </BoardSelect>
    );
  },
);

PrivateBoardSelect.displayName = 'PrivateBoardSelect';
